'use client';

import useToggleState from '@lib/hooks/use-toggle-state';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { getFeatureFlagState } from '@lib/data';
import { usePathname } from 'next/navigation';
import { featureFlags } from '@lib/constants/network-constants';

interface CartDropdownContext {
    state: boolean;
    open: () => void;
    timedOpen: () => void;
    close: () => void;
    pointMode: boolean;
    totalPoints: number,
    setTotalPoints: (points: number) => void;
}

export const CartDropdownContext = createContext<CartDropdownContext | null>(
    null,
);

export const CartDropdownProvider = ({
                                         children,
                                     }: {
    children: React.ReactNode;
}) => {
    const pathname = usePathname();

    const { state, close, open } = useToggleState();
    const [activeTimer, setActiveTimer] = useState<NodeJS.Timer | undefined>(
        undefined,
    );

    const [isPointMode, setIsPointMode] = useState<boolean>(false);
    const [totalPoints, setTotalPoints] = useState<number>(0);
    const timedOpen = () => {
        open();

        const timer = setTimeout(close, 5000);

        setActiveTimer(timer);
    };

    const openAndCancel = () => {
        if (activeTimer) {
            clearTimeout(activeTimer);
        }

        open();
    };

    // Clean up the timer when the component unmounts
    useEffect(() => {
        return () => {
            if (activeTimer) {
                clearTimeout(activeTimer);
            }
        };
    }, [activeTimer]);

    const checkPointMode = async () => {
        try {
            const pointMode = await getFeatureFlagState(featureFlags.POINTS_MODE);
            setIsPointMode(pointMode);
            if (pointMode) {
                setTotalPoints(22555);
            }
        } catch (error) {
        }
    };

    useEffect(() => {
        if (!pathname.includes('redemption')){
            checkPointMode();
        }
    }, []);

    const contextValue = useMemo(
        () => ({
            state,
            close,
            open: openAndCancel,
            timedOpen,
            pointMode: isPointMode,
            totalPoints: totalPoints,
            setTotalPoints: setTotalPoints
        }),
        [state, close, openAndCancel, timedOpen, isPointMode, totalPoints, setTotalPoints],
    );

    return (
        <CartDropdownContext.Provider value={contextValue}>
            {children}
        </CartDropdownContext.Provider>
    );
};

export const useCartDropdown = () => {
    const context = useContext(CartDropdownContext);

    if (context === null) {
        throw new Error(
            'useCartDropdown must be used within a CartDropdownProvider',
        );
    }

    return context;
};
